import { useEffect, useState, useRef } from "react";
import { getDashboardStats, getRTDashboardStats } from "../../Api Methods/Api";
import { useDispatch, useSelector } from "react-redux";
import { Doughnut } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import Stats2 from "../../components/UI/Stats/Stats2";
import classes from './Dashboard.module.css';
import mixpanel from "mixpanel-browser";

ChartJS.register(ArcElement, Tooltip, Legend);

const centerTextPlugin = {
    id: "centerText",
    beforeDraw(chart) {
        const cfg = chart.config.options.plugins.centerText;
        if (!cfg?.show) return;
        const { ctx, chartArea } = chart;
        const cx = (chartArea.left + chartArea.right) / 2;
        const cy = (chartArea.top + chartArea.bottom) / 2;
        ctx.save();
        ctx.font = `600 16px -apple-system, BlinkMacSystemFont, sans-serif`;
        ctx.fillStyle = "#5c5c5c";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(cfg.text, cx, cy);
        ctx.restore();
    },
};
ChartJS.register(centerTextPlugin);

const C = {
    critical: "#df7474",
    warning: "#efbc65",
    healthy: "#5fdc8d",
    online: "#60e08f",
    offline: "#ee6d6d",
    stale: "#fec665",
    potentiallyStale: "#e8944f",
    powerDown: "#d95d5d",
    unknown: "#9ca3af",
    mains: "#6298ee",
    solar: "#b2e368",
    battery: "#f3b853",
    poor: "#d95d5d",
    fair: "#e8944f",
    good: "#5fdc8d",
};

const makeOptions = (total) => ({
    responsive: true,
    maintainAspectRatio: false,
    cutout: "70%",
    plugins: {
        legend: {
            position: "bottom",
            labels: {
                font: { size: 11 },
                padding: 10,
                boxWidth: 10,
                usePointStyle: true,
                generateLabels: (chart) => {
                    const { labels, datasets } = chart.data;
                    return labels.map((label, i) => ({
                        text: `${label}  ${datasets[0].data[i].toLocaleString()}`,
                        fillStyle: datasets[0].backgroundColor[i],
                        strokeStyle: datasets[0].backgroundColor[i],
                        pointStyle: "circle",
                        hidden: false,
                        index: i,
                    }));
                },
            },
        },
        tooltip: { enabled: true },
        centerText: { show: true, text: total.toLocaleString() },
    },
});

const DonutCard = ({ title, labels, values, colors }) => {
    const total = values.reduce((a, b) => a + b, 0);
    const data = {
        labels,
        datasets: [{ data: values, backgroundColor: colors, borderWidth: 0, hoverOffset: 4 }],
    };
    return (
        <div className={classes.chartCard}>
            <p className={classes.chartTitle}>{title}</p>
            <div className={classes.donutContainer}>
                <Doughnut data={data} options={makeOptions(total)} />
            </div>
        </div>
    );
};

const SkeletonCard = () => (
    <div className={classes.skeletonCard}>
        <div className={classes.skeletonTop}>
            <div className={classes.skeletonBar} style={{ height: "18px", width: "60%" }} />
            <div className={classes.skeletonBar} style={{ height: "24px", width: "24px", borderRadius: "50%" }} />
        </div>
        <div className={classes.skeletonBar} style={{ height: "32px", width: "40%" }} />
    </div>
);

const SkeletonChart = () => (
    <div className={classes.chartCard}>
        <div className={classes.skeletonBar} style={{ height: "14px", width: "55%", marginBottom: "16px" }} />
        <div className={classes.skeletonCircle} />
        <div className={classes.skeletonBar} style={{ height: "10px", width: "80%", marginTop: "14px" }} />
    </div>
);

const RTSectionHeader = ({ title, lastRTUpdate, secondsAgo, formatSecondsAgo }) => (
    <div className={classes.sectionHeader}>
        <p className={classes.sectionTitle}>{title}</p>
        {lastRTUpdate && (
            <span className={classes.lastUpdated}>Updated {formatSecondsAgo(secondsAgo)}</span>
        )}
    </div>
);

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [RTStats, setRTStats] = useState(null);
    const [lastRTUpdate, setLastRTUpdate] = useState(null);
    const [secondsAgo, setSecondsAgo] = useState(0);
    const client = useSelector(state => state.auth.client);
    const token = useSelector(state => state.auth.token);
    const apiDataRef = useRef({ token, client });
    apiDataRef.current = { token, client };
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [isLoadingRT, setIsLoadingRT] = useState(true);

    useEffect(() => {
        const HandleGetStats = async () => {
            const response = await getDashboardStats(apiDataRef.current, dispatch);
            setStats(response.data[0][0]);
            setIsLoading(false);
        };

        const HandleGetRTStats = async () => {
            const response = await getRTDashboardStats(apiDataRef.current, dispatch);
            setRTStats(response.data[0][0]);
            setIsLoadingRT(false);
            setLastRTUpdate(Date.now());
        };

        mixpanel.track("Dashboard visited", { user: localStorage.getItem("email") });
        HandleGetStats();
        HandleGetRTStats();

        const pollInterval = setInterval(HandleGetRTStats, 60000);
        return () => clearInterval(pollInterval);
    }, []);

    useEffect(() => {
        if (!lastRTUpdate) return;
        setSecondsAgo(0);
        const tick = setInterval(() => {
            setSecondsAgo(Math.floor((Date.now() - lastRTUpdate) / 1000));
        }, 1000);
        return () => clearInterval(tick);
    }, [lastRTUpdate]);

    const formatSecondsAgo = (s) => {
        if (s < 60) return `${s}s ago`;
        return `${Math.floor(s / 60)}m ${s % 60}s ago`;
    };

    return (
        <div className={classes.pageContainer}>

            <div className={classes.heroRow}>
                <div>
                    <p className={classes.sectionTitle}>Portfolio overview</p>
                    <div className={classes.portfolioGrid}>
                        {isLoading ? [...Array(7)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                            <Stats2 title={"All sites"} value={stats.sitesCount} isLoadingStats={false} isStatic={true} />
                            <Stats2 title={"Active sites"} value={stats.activeCount} isLoadingStats={false} isStatic={true} />
                            <Stats2 title={"Inactive sites"} value={stats.inactiveCount} isLoadingStats={false} isStatic={true} />
                            <Stats2 title={"Sites on schedule"} value={stats.scheduleCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeScheduleCount} />
                            <Stats2 title={"Live view enabled sites"} value={stats.liveViewSitesCount} isLoadingStats={false} isStatic={true} />
                            <Stats2 title={"Groups"} value={stats.accountsCount} isLoadingStats={false} isStatic={true} />
                            <Stats2 title={"Users"} value={stats.totalUserCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeTotalUserCount} />

                        </>}
                    </div>
                </div>

                <div>
                    <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="Site health" />
                    <div className={classes.siteHealthRow}>
                        <div className={classes.siteHealthDonut}>
                            {isLoadingRT ? <SkeletonChart /> : RTStats &&
                                <DonutCard
                                    title="Online vs offline"
                                    labels={["Online", "Offline"]}
                                    values={[RTStats.onlineSites, RTStats.offlineSites]}
                                    colors={[C.online, C.offline]}
                                />
                            }
                        </div>
                        <div className={classes.siteHealthStats}>
                            {isLoading ? [...Array(3)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                                <Stats2 title={"Controllers"} value={stats.controllerCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeControllerCount} />
                                <Stats2 title={"Offline controllers"} value={stats.offlineCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.offlineActiveCount} />
                                <Stats2 title={"Active schedule only sites"} value={stats.scheduleOnlyCount} isLoadingStats={false} isStatic={true} />
                            </>}
                        </div>
                    </div>
                </div>
            </div>

            <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="Device health" />
            <div className={classes.container}>
                {isLoading ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                    <Stats2 title={"Padlocks"} value={stats.lockCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeLockCount} />
                    <Stats2 title={"Keyboxes"} value={stats.keyboxCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeKeyboxCount} />
                    <Stats2 title={"Sensors"} value={stats.sensorCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeSensorCount} />
                    <Stats2 title={"Gateways"} value={stats.gatewayCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeGatewayCount} />
                </>}
            </div>
            <div className={classes.chartsGrid}>
                {isLoadingRT ? [...Array(2)].map((_, i) => <SkeletonChart key={i} />) : RTStats && <>
                    <DonutCard
                        title="Padlock battery health"
                        labels={["Critical (<5%)", "Warning (<20%)", "Healthy (>20%)"]}
                        values={[RTStats.criticalLocks, RTStats.warningLocks, RTStats.healthyLocks]}
                        colors={[C.critical, C.warning, C.healthy]}
                    />
                    <DonutCard
                        title="Keybox battery health"
                        labels={["Critical (<5%)", "Warning (<20%)", "Healthy (>20%)"]}
                        values={[RTStats.criticalKeyboxes, RTStats.warningKeyboxes, RTStats.healthyKeyboxes]}
                        colors={[C.critical, C.warning, C.healthy]}
                    />
                </>}
            </div>

            <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="Gateway health" />
            <div className={classes.chartsGrid}>
                {isLoadingRT ? [...Array(4)].map((_, i) => <SkeletonChart key={i} />) : RTStats && <>
                    <DonutCard
                        title="Connectivity status"
                        labels={["Online", "1 missed poll", "2 missed polls", "Powered down", "Unknown"]}
                        values={[RTStats.onlineGateways, RTStats.potentiallyStaleGateways, RTStats.staleGateways, RTStats.powerDownGateways, RTStats.unknownGateways]}
                        colors={[C.online, C.potentiallyStale, C.stale, C.powerDown, C.unknown]}
                    />
                    <DonutCard
                        title="Battery health"
                        labels={["Critical (<5%)", "Warning (<20%)", "Healthy (>20%)"]}
                        values={[RTStats.criticalGateways, RTStats.warningGateways, RTStats.healthyGateways]}
                        colors={[C.critical, C.warning, C.healthy]}
                    />
                    <DonutCard
                        title="Power source"
                        labels={["Mains", "Solar", "Battery"]}
                        values={[RTStats.mainsPoweredGateways, RTStats.solarPoweredGateways, RTStats.batteryPoweredGateways]}
                        colors={[C.mains, C.solar, C.battery]}
                    />
                    <DonutCard
                        title="Signal quality"
                        labels={["Poor", "Fair", "Good"]}
                        values={[RTStats.poorSignalGateways, RTStats.fairSignalGateways, RTStats.goodSignalGateways]}
                        colors={[C.poor, C.fair, C.good]}
                    />
                </>}
            </div>

            <RTSectionHeader lastRTUpdate={lastRTUpdate} secondsAgo={secondsAgo} formatSecondsAgo={formatSecondsAgo} title="CCTV & integrations" />
            <div className={classes.container}>
                {isLoading ? [...Array(6)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                    <Stats2 title={"Total integrations"} value={stats.totalIntegrations} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.totalActiveIntegrations} />
                    <Stats2 title={"AJAX"} value={stats.ajaxCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeAjaxCount} />
                    <Stats2 title={"Dahua"} value={stats.dahuaCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeDahuaCount} />
                    <Stats2 title={"Hikvision"} value={stats.hikvisionCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeHikvisionCount} />
                    <Stats2 title={"AIBox"} value={stats.aiboxCount} isLoadingStats={false} isStatic={true} hasSecondValue={true} value2={stats.activeAiboxCount} />
                    {RTStats && <Stats2 title={"Unstable CCTV connections"} value={RTStats.unstableCCTV} isLoadingStats={false} isStatic={true} />}
                </>}
            </div>

            <p className={classes.sectionTitle}>Billing</p>
            <div className={classes.container} style={{ marginBottom: "24px" }}>
                {isLoading ? [...Array(4)].map((_, i) => <SkeletonCard key={i} />) : stats && <>
                    <Stats2 title={"Billable sites"} value={stats.activeCount} isLoadingStats={false} isStatic={true} />
                    <Stats2 title={"Billable padlocks"} value={stats.billedLockCount} isLoadingStats={false} isStatic={true} />
                    <Stats2 title={"Billable keyboxes"} value={stats.billedKeyboxCount} isLoadingStats={false} isStatic={true} />
                    <Stats2 title={"Billable gateways"} value={stats.billedGatewayCount} isLoadingStats={false} isStatic={true} />
                </>}
            </div>

        </div>
    );
};

export default Dashboard;
